import { COSIGN_X_PLATFORM_TOKEN } from '@constants'
import { getCampaignCodeFromPath } from '@utils'
import { createMutationHelper, createRequestHelper } from '@utils/swr'
import { type AxiosInstance, type AxiosResponse } from 'axios'
import Cookies from 'js-cookie'
import { type Middleware } from 'swr'
import { type RefreshCosignTokenRequest } from '../types/apiRequest'
import { type RefreshCosignTokenResponse } from '../types/apiResponse'
import { type FortuneApis } from '../types/apis'

const {
  useRequest,
  mutate,
} = createRequestHelper<FortuneApis['get']['platformToken'], RefreshCosignTokenResponse, RefreshCosignTokenRequest>({
  client: 'fortune',
  key: params => params?.code ? `/campaigns/${params.code}/member/platform_token` : null,
})

const {
  useMutation,
} = createMutationHelper<FortuneApis['get']['platformToken'], RefreshCosignTokenResponse, RefreshCosignTokenRequest, { code: string }>({
  client: 'fortune',
  key: params => params?.code ? `/campaigns/${params.code}/member/platform_token` : null,
  fetcher: (url, { arg, client }) => client.get(`${url}?code=${arg.code}/member/platform_token`),
})

export const useApiMutationCosignToken = useMutation
export const refreshCosignToken = useRequest
export const refreshCosignTokenManually = mutate
export const createWithUpdateCosignToken = (clients: AxiosInstance[]): Middleware => (useSWRNext) => {
  return (key, fetcher, config) => {
    const swr = useSWRNext(key, fetcher, {
      ...config,
      onSuccess: (...args) => {
        const token = (args[0] as AxiosResponse<RefreshCosignTokenResponse>).data.data[COSIGN_X_PLATFORM_TOKEN]
        if (token) {
          const code = getCampaignCodeFromPath()
          if (code) {
            Cookies.set(`${code}_${COSIGN_X_PLATFORM_TOKEN}`, token)
            const X_PLATFORM_TOKEN = 'x-platform-token'
            clients.forEach((client) => {
              client.defaults.headers[X_PLATFORM_TOKEN] = token
            })
          }
        }
        // FIXME: Need better error type for Middleware

        config?.onSuccess?.(...args)
      },
    })

    return swr
  }
}

/**
 * STM 專用的 xpt refresh middleware。
 * refresh 成功後將新的 x-platform-token 寫入 stm_ 前綴的 cookie。
 * - clients 為 optional：登入前（UnauthRoutes）需傳入 cosign client 以更新 defaults.headers，
 *   因為 cosign client 沒有 STM interceptor，需靠 headers 帶 xpt。
 *   登入後（AuthRoutes）不需傳入，因為 fortune 等 client 由 interceptor 動態從 cookie 讀取。
 */
export const createStmWithUpdateCosignToken = (campaignCode: string, clients?: AxiosInstance[]): Middleware => (useSWRNext) => {
  return (key, fetcher, config) => {
    const swr = useSWRNext(key, fetcher, {
      ...config,
      onSuccess: (...args) => {
        const token = (args[0] as AxiosResponse<RefreshCosignTokenResponse>).data.data[COSIGN_X_PLATFORM_TOKEN]
        if (token) {
          Cookies.set(`stm_${campaignCode}_${COSIGN_X_PLATFORM_TOKEN}`, token)
          if (clients) {
            const X_PLATFORM_TOKEN = 'x-platform-token'
            clients.forEach((client) => {
              client.defaults.headers[X_PLATFORM_TOKEN] = token
            })
          }
        }
        config?.onSuccess?.(...args)
      },
    })
    return swr
  }
}
